'use client';

import type { FC } from 'react';
import type { BreadcrumbItemType } from '@/components/shared';
import type { SystemPageDto } from '@/queries/system-pages';
import { useTranslations } from 'next-intl';
import { parseAsInteger } from 'nuqs';
import { useState } from 'react';
import { AuthorCard } from '@/components/entities/author/AuthorCard';
import { GamesSearchBar } from '@/components/entities/games/GamesSearchBar';
import { SeoPagesTopSidebar } from '@/components/entities/seoPages/SeoPagesTopSidebar';
import { AppBreadcrumbs } from '@/components/shared';
import { FaqAccordionSection } from '@/components/ui/Accordion/FaqAccordion';
import { useQueryStateWithoutSuspense } from '@/hooks/useQueryStateWithoutSuspense';
import { getPlateContentState } from '@/utils/parsing.utils';
import { AboutProvider } from './AboutProvider';
import ProviderList from './ProviderList';

const breadcrumbsItems: BreadcrumbItemType[] = [
  { href: '/', label: 'All games', icon: 'all-games' },
  { label: 'Providers' },
];

interface ProvidersViewProps {
  providersSeoData: SystemPageDto;
}

export const ProvidersView: FC<ProvidersViewProps> = ({ providersSeoData }) => {
  const t = useTranslations('providers.providers-list');

  const content = providersSeoData?.body;
  const { value: plateContentValue, hasTextContent } = getPlateContentState(content);

  const [searchQuery, setSearchQuery] = useState('');
  const [pageParam, setPageParam] = useQueryStateWithoutSuspense('page', parseAsInteger.withDefault(1));

  return (
    <div className="max-w-282 justify-center flex gap-10 w-full mx-auto mb-10">
      <div className="max-w-221 w-full xxl:shrink-0 relative">
        <AppBreadcrumbs items={breadcrumbsItems} className="lg:sr-only h-0" />

        <div className="flex flex-col gap-13 mt-1.5 md:mt-0 md:gap-17">

          <hgroup className="tracking-normal">
            <div className="flex justify-between items-center md:items-start md:mt-px">

              <h1 className="font-medium text-[1.75rem] tracking-normal leading-[1.2] capitalize text-white">
                {providersSeoData.title}
              </h1>
              <GamesSearchBar
                placeholder={t('search-placeholder')}
                onSearchChange={setSearchQuery}
              />

            </div>

            <p className="w-full mt-5.25 md:mt-6.5 text-lg leading-7 tracking-normal text-c300">{providersSeoData.description}</p>
          </hgroup>

          <ProviderList
            page={pageParam || 1}
            search={searchQuery}
            onSetPage={setPageParam}
          />
          {
            hasTextContent && <AboutProvider contentValue={plateContentValue} />
          }

          <SeoPagesTopSidebar className="xl:hidden grid grid-cols-2 gap-3" />

          {
            providersSeoData?.faqs && providersSeoData.faqs.length > 0 && (
              <FaqAccordionSection
                className="max-w-200"
                faqsList={providersSeoData.faqs}
              />
            )
          }

          {providersSeoData?.author && <AuthorCard className="max-w-200" author={providersSeoData.author} />}
        </div>
      </div>

      <SeoPagesTopSidebar className="w-full hidden xl:flex flex-col mt-0.5 gap-10 max-w-50 sticky top-0 self-start" />
    </div>
  );
};
